import type { WorksheetType } from "../../../shared/types/worksheet";

import { globalShareDBState } from "providers/ShareDBProvider";
import { docCache } from "sharedb/ShareDBDoc";

/**
 * Get the worksheet data for a given worksheet id and version number.
 * Passing null for worksheetVersionNumber will fetch the latest version of the worksheet.
 */
const docSnapshotDataPromise = (
  docId: string,
  worksheetVersionNumber: number | null,
): Promise<WorksheetType> => {
  return new Promise((resolve, reject) => {
    const connection = globalShareDBState.connection;
    if (!connection) {
      reject(new Error("No connection to ShareDB"));
      return;
    }

    connection.fetchSnapshot(
      window._env_.SHAREDB_WORKSHEET_COLLECTION,
      docId,
      worksheetVersionNumber,
      (err, fetchedSnapshot) => {
        if (err) {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
          reject(err);
        } else {
          resolve(fetchedSnapshot.data as WorksheetType);
        }
      },
    );
  });
};

const docDataCache = new Map<string, WorksheetType>();

export const getOrFetchDocData = async (
  worksheetId: string,
  shareDBVersion: number | null = null,
) => {
  const cacheId = `${worksheetId}-${shareDBVersion}`;
  let docData = docDataCache.get(cacheId);
  if (docData) {
    return docData;
  }

  const cachedLiveDoc = docCache[cacheId];
  if (cachedLiveDoc) {
    docData = cachedLiveDoc.data;
  } else if (!docData) {
    docData = await docSnapshotDataPromise(worksheetId, shareDBVersion);
  }

  if (docData) {
    docDataCache.set(cacheId, docData);
  }
  return docData;
};
